/**
 * O DICIONARIO DA AJUDA, em ingles.
 *
 * O DEFEITO QUE ELE FECHA, medido em 09/09/2026 pelo caso `qa-plt-ajuda-16`: com o idioma
 * em EN, o chassi trocava e o `main` NAO mudava um caractere. O caso gravou
 * "seletor presente: true, texto mudou: false", que e a assinatura exata de metade da
 * interface trocando, e a regra dura de bilingue diz que isso e PIOR que seletor nenhum,
 * porque a pessoa passa a desconfiar do que leu.
 *
 * A CAUSA era simples e invisivel: `packages/help/package.json:23` ja declarava
 * `@complexity/i18n` como dependencia, e `grep -c "@complexity/i18n"` nos dois componentes
 * devolvia ZERO. A dependencia estava comprada e nunca foi usada.
 *
 * POR QUE ELE MORA AQUI, E NAO NO `en.ts` DE CADA PRODUTO, e a razao esta escrita no proprio
 * mecanismo (`packages/i18n/src/index.ts:70`): "cada pacote e cada produto chama isto com as
 * proprias frases, em vez de existir um arquivo gigante central". Estas frases sao da AJUDA,
 * e a Ajuda e capacidade TRANSVERSAL: ela aparece identica em todo produto da plataforma.
 * Copiadas para o `en.ts` de cada app, seriam a mesma traducao em N lugares, o N-esimo
 * envelheceria, e o produto que nascesse amanha nasceria com a Ajuda em portugues no modo
 * ingles. Isso e literalmente o item 2 da regra das cinco camadas: "nenhuma regra e copiada
 * para dentro do app".
 *
 * A CHAVE E A FRASE EM PORTUGUES, como em toda esta casa. A consequencia pratica que importa:
 * frase sem traducao cadastrada DEGRADA para o portugues, que e legivel, em vez de virar
 * `ajuda.busca.placeholder` na tela de quem usa.
 *
 * O QUE NAO ENTRA AQUI, e a lista e curta e deliberada:
 *   . O CONTEUDO PUBLICADO. Titulo de artigo, resumo, corpo e nome de categoria NAO passam
 *     por `t()`, e desde o ADR 0060 isso deixou de significar que eles ficam em portugues: a
 *     versao em ingles mora no BANCO (`conhecimento_artigos_traducoes` e
 *     `conhecimento_categorias_traducoes`, migration 0077), e o servico a devolve quando a
 *     tela pede `?idioma=en`. Dicionario de frases nao e lugar de manual inteiro, e cada
 *     artigo novo precisaria de um deploy de front para ganhar ingles.
 *   . O ASSUNTO E A MENSAGEM QUE A PROPRIA PESSOA ESCREVEU na conversa. Esses continuam fora
 *     de qualquer traducao, pelo item "conteudo escrito pelo proprio usuario" de
 *     `NAO_TRADUZIR`.
 *   . A MENSAGEM DE ERRO VINDA DA API. Ela chega como texto do servidor e atravessa `t()`
 *     intacta, pela degradacao acima.
 */
import type { Dicionario } from '@complexity/i18n';

export const EN_AJUDA: Dicionario = {
  // --- O cabecalho da Central e a busca (blocos 1 e 2 da tela) --------------
  'Central de Ajuda': 'Help Center',
  // O aviso de que o artigo ainda nao tem traducao e chegou no original (ADR 0060). Em
  // portugues ele nunca aparece, porque ali o original ja e o idioma pedido.
  'Disponível só em português': 'Available in Portuguese only',
  'Base de conhecimento da plataforma: busque e veja a resposta na hora, sem esperar.':
    'Platform knowledge base: search and see the answer right away, no waiting.',
  'Buscar na ajuda': 'Search help',
  'Buscar ajuda…': 'Search help…',
  Resultados: 'Results',
  Categorias: 'Categories',
  'Todas as categorias': 'All categories',
  Voltar: 'Back',
  'Carregando…': 'Loading…',

  // --- Os estados vazios, que sao AUSENCIA e nao falha ----------------------
  // A regra dura de 07/09/2026 separa as duas coisas, e a traducao preserva a separacao:
  // estas frases dizem "olhei e nao ha", e nunca "nao consegui olhar".
  'Nenhum artigo para essa busca': 'No articles for that search',
  'Tente outras palavras, ou descreva o problema abaixo para a equipe.':
    'Try other words, or describe the problem below for the team.',
  'Nenhuma categoria publicada ainda': 'No categories published yet',
  'Quando a equipe publicar o primeiro artigo, ele aparece aqui.':
    'When the team publishes the first article, it shows up here.',

  // --- Os estados de INDISPONIVEL, que sao falha de verdade -----------------
  'A base de conhecimento não respondeu.': 'The knowledge base did not respond.',
  'A base de conhecimento não respondeu. Tente novamente em instantes.':
    'The knowledge base did not respond. Please try again shortly.',
  'A busca não respondeu. Tente novamente em instantes.':
    'The search did not respond. Please try again shortly.',
  'Os artigos desta categoria não responderam.':
    'The articles in this category did not respond.',
  'Namespace de ajuda não configurado para este produto.':
    'Help namespace not configured for this product.',

  // --- O bloco "nao encontrei" e o formulario de conversa -------------------
  'Não encontrei o que precisava': 'I did not find what I needed',
  'Descreva que a gente responde por aqui.': 'Describe it and we will answer here.',
  Fechar: 'Close',
  'Descrever o problema': 'Describe the problem',
  Tipo: 'Type',
  'Selecione…': 'Select…',
  Categoria: 'Category',
  'Escolha da lista ou sugira uma nova': 'Pick from the list or suggest a new one',
  Assunto: 'Subject',
  'Resuma em poucas palavras': 'Sum it up in a few words',
  Mensagem: 'Message',
  'Conte com detalhes o que você precisa…': 'Tell us in detail what you need…',
  Imagens: 'Images',
  'Enviando…': 'Sending…',
  Enviar: 'Send',
  'Não foi possível enviar agora. Tente novamente.':
    'Could not send right now. Please try again.',
  'A sua mensagem foi enviada, mas não consegui anexar as imagens. Você pode respondê-la com as imagens depois.':
    'Your message was sent, but the images could not be attached. You can reply with the images later.',

  // --- Minhas solicitacoes, o fio da conversa -------------------------------
  'Minhas solicitações': 'My requests',
  Equipe: 'Team',
  Você: 'You',
  'Abrindo…': 'Opening…',
  'A nossa equipe responde por aqui. Você recebe um aviso quando houver resposta.':
    'Our team answers here. You get a notice when there is a reply.',

  // --- OS OITO ROTULOS de `central.ts` -------------------------------------
  // Eles sao traduzidos NO PONTO DE USO, e nao na constante: `ROTULO_DO_TIPO` e
  // `ROTULO_DA_SITUACAO` sao `Object.freeze` avaliados na CARGA DO MODULO, antes de existir
  // qualquer escolha de idioma. Traduzidos la, congelariam o idioma do primeiro import.
  Dúvida: 'Question',
  Suporte: 'Support',
  Reclamação: 'Complaint',
  Elogio: 'Compliment',
  Sugestão: 'Suggestion',
  'Em análise': 'Under review',
  Respondida: 'Answered',
  Encerrada: 'Closed',

  // --- A GAVETA de busca rapida (`PainelAjuda`) ----------------------------
  // Ela e outra tela que a Central: a gaveta e a busca rapida dentro do chassi, e a Central
  // e a tela inteira. As duas atravessam os mesmos produtos, entao as duas moram aqui.
  Ajuda: 'Help',
  'Fechar a ajuda': 'Close help',
  'O que você procura?': 'What are you looking for?',
  'Digite para buscar nos artigos deste produto.':
    'Type to search the articles of this product.',
  'Buscando…': 'Searching…',
  'A ajuda não respondeu': 'Help did not respond',
  'O conteúdo existe, mas não conseguimos buscá-lo agora.':
    'The content exists, but we could not fetch it right now.',
  'Nenhum artigo para esse termo': 'No articles for that term',
  'Falar com a nossa equipe': 'Talk to our team',
  'A busca funcionou e não encontrou nada com esse termo. Tente outra palavra.':
    'The search worked and found nothing for that term. Try another word.',
  'Voltar aos resultados': 'Back to results',
  'Não conseguimos carregar este artigo agora.': 'We could not load this article right now.',
};
